import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { StatusBadge } from "../components/etams/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatIDR } from "../lib/format";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../components/etams/PalangDaftar";

const KOLOM_CSV = [
  { kunci: "sort_order", judul: "Sort" },
  { kunci: "collar_code", judul: "Code" },
  { kunci: "collar_name", judul: "Nama Collar" },
  { kunci: "surcharge_amount_idr", judul: "Surcharge (IDR)" },
  { kunci: "effective_from", judul: "Berlaku Dari" },
  { kunci: "effective_to", judul: "Berlaku Sampai" },
  { kunci: "active", judul: "Aktif", ambil: (c) => (c.active ? "Ya" : "Tidak") },
];


export default function MasterCollar() {
  const { isOwner } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/master/collar"); setItems(r.data.items || []); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Gagal memuat"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const daftar = useDaftar(items, {
    kunciCari: ["collar_code", "collar_name"],
    urutAwal: { kunci: "sort_order", arah: "asc" },
  });

  const handleDelete = async () => {
    try { await api.delete(`/master/collar/${confirmDel.id}`); toast.success("Collar dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Gagal hapus"); }
    finally { setConfirmDel(null); }
  };

  return (
    <div className="space-y-6" data-testid="master-collar-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Collar / Kerah</h1>
          <p className="text-sm text-muted-foreground mt-1">Surcharge tipe kerah per pcs jersey.</p>
          {/* T111d (2 Nov 2026): satu baris penjelas supaya tidak tertukar dengan tambahan UPAH per kerah. */}
          <p className="text-sm text-muted-foreground mt-1" data-testid="penjelas-kerah-harga-jual">
            Angka di sini menambah <strong>harga jual ke customer</strong>. Tambahan <strong>upah jahit</strong> per
            kerah (dibayar ke penjahit) diatur terpisah di Master › Tarif Borongan.
          </p>
        </div>
        {isOwner && <Button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="collar-new-btn"><Plus size={16} className="mr-1" /> Tambah</Button>}
      </div>
      <Card className="p-4">
        <PalangDaftar daftar={daftar} placeholder="Cari code / nama collar…" testId="collar-palang"
                      ekspor={{ nama: "master_collar", kolom: KOLOM_CSV }} />
        {loading ? <div className="py-8 text-center text-sm text-muted-foreground">Memuat...</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                <tr>
                  <ThUrut daftar={daftar} kunci="sort_order" className="text-left px-3 py-2">Sort</ThUrut>
                  <ThUrut daftar={daftar} kunci="collar_code" className="text-left px-3 py-2">Code</ThUrut>
                  <ThUrut daftar={daftar} kunci="collar_name" className="text-left px-3 py-2">Nama Collar</ThUrut>
                  <ThUrut daftar={daftar} kunci="surcharge_amount_idr" className="text-right px-3 py-2" align="right">Surcharge (IDR)</ThUrut>
                  <th className="text-left px-3 py-2">Berlaku</th>
                  <ThUrut daftar={daftar} kunci="active" className="text-left px-3 py-2">Status</ThUrut>
                  {isOwner && <th></th>}
                </tr>
              </thead>
              <tbody>
                {daftar.total === 0 && <tr><td colSpan={isOwner ? 7 : 6} className="px-3 py-8 text-center text-muted-foreground">
                  {daftar.cari.trim() ? "Tidak ada yang cocok." : "Belum ada collar."}
                </td></tr>}
                {daftar.tampil.map((c) => (
                  <tr key={c.id} className="border-t border-border" data-testid={`collar-row-${c.id}`}>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{c.sort_order}</td>
                    <td className="px-3 py-2 font-mono text-xs">{c.collar_code}</td>
                    <td className="px-3 py-2 font-medium">{c.collar_name}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatIDR(c.surcharge_amount_idr)}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{c.effective_from || "—"} → {c.effective_to || "∞"}</td>
                    <td className="px-3 py-2">{c.active ? <StatusBadge tone="success">Aktif</StatusBadge> : <Badge variant="outline">Nonaktif</Badge>}</td>
                    {isOwner && (
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <Button variant="ghost" size="icon" onClick={() => { setEditing(c); setShowForm(true); }} data-testid={`collar-edit-${c.id}`}><Edit size={14} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => setConfirmDel(c)} className="text-danger-soft-foreground" data-testid={`collar-delete-${c.id}`}><Trash2 size={14} /></Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <Paginasi daftar={daftar} testId="collar-paginasi" />
          </div>
        )}
      </Card>
      {showForm && <CollarFormModal collar={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={() => { setShowForm(false); setEditing(null); load(); }} />}
      <AlertDialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Hapus Collar?</AlertDialogTitle><AlertDialogDescription>"{confirmDel?.collar_name}" akan dihapus.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="collar-delete-confirm">Hapus</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CollarFormModal({ collar, onClose, onSaved }) {
  const [f, setF] = useState({
    collar_code: collar?.collar_code || "",
    collar_name: collar?.collar_name || "",
    surcharge_amount_idr: collar?.surcharge_amount_idr ?? 0,
    sort_order: collar?.sort_order ?? 0,
    effective_from: collar?.effective_from || "",
    effective_to: collar?.effective_to || "",
    active: collar?.active ?? true,
    notes: collar?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    if (!f.collar_code.trim() || !f.collar_name.trim()) { toast.error("Code dan Nama wajib"); return; }
    setSaving(true);
    try {
      const body = { ...f, surcharge_amount_idr: parseInt(f.surcharge_amount_idr) || 0, sort_order: parseInt(f.sort_order) || 0,
        effective_from: f.effective_from || null, effective_to: f.effective_to || null, notes: f.notes || null };
      if (collar) await api.patch(`/master/collar/${collar.id}`, body);
      else await api.post("/master/collar", body);
      toast.success("Disimpan"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || "Gagal simpan"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="collar-form-modal">
        <DialogHeader><DialogTitle>{collar ? "Edit Collar" : "Tambah Collar"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div><Label>Code *</Label><Input value={f.collar_code} onChange={(e) => upd("collar_code", e.target.value)} placeholder="cth: O_NECK" data-testid="collar-form-code" /></div>
          <div><Label>Nama Collar *</Label><Input value={f.collar_name} onChange={(e) => upd("collar_name", e.target.value)} placeholder="cth: O-Neck Polos" data-testid="collar-form-name" /></div>
          <div><Label>Surcharge (IDR) *</Label><Input type="number" min={0} value={f.surcharge_amount_idr} onChange={(e) => upd("surcharge_amount_idr", e.target.value)} data-testid="collar-form-amount" /></div>
          <div><Label>Sort Order</Label><Input type="number" value={f.sort_order} onChange={(e) => upd("sort_order", e.target.value)} /></div>
          <div><Label>Berlaku Dari</Label><Input type="date" value={f.effective_from} onChange={(e) => upd("effective_from", e.target.value)} /></div>
          <div><Label>Berlaku Sampai</Label><Input type="date" value={f.effective_to} onChange={(e) => upd("effective_to", e.target.value)} /></div>
          <div className="col-span-2 flex items-center gap-2"><Checkbox checked={f.active} onCheckedChange={(v) => upd("active", !!v)} id="clactive" /><Label htmlFor="clactive" className="cursor-pointer">Aktif</Label></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving} data-testid="collar-form-save">{saving ? "Menyimpan..." : "Simpan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
